import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { DirectMediaUploadService, uploadErrorMessage } from 'src/app/core/direct-media-upload.service';
import {
  MAX_UPLOAD_SIZE_MB,
  getUploadRejection,
  supportedUploadFormats,
  uploadAccept,
} from 'src/app/core/media-upload';

interface MediaItem {
  file: File;
  objectUrl: string;   // revoked when the item goes away
  previewUrl: SafeUrl; // objectUrl, trusted so Angular allows it on <img>/<video>
  isVideo: boolean;
}

@Component({
  selector: 'app-create-media',
  templateUrl: './create-media.component.html',
  styleUrls: ['./create-media.component.scss'],
})
export class CreateMediaComponent implements OnInit, OnDestroy {

  readonly MAX_FILES = 10;
  readonly MAX_UPLOAD_SIZE_MB = MAX_UPLOAD_SIZE_MB;
  readonly UPLOAD_ACCEPT = uploadAccept();
  readonly SUPPORTED_FORMATS = supportedUploadFormats();

  // Per-field character limits (enforced via the native maxlength attribute in
  // the template and mirrored here as validators / title truncation).
  readonly TITLE_MAX = 15;
  readonly CAPTION_MAX = 25;
  readonly ALT_MAX = 25;

  mediaForm: FormGroup;
  mediaItems: MediaItem[] = [];

  isUploading = false;
  uploadStatus = ''; // shown on the overlay while uploading
  imagesError = false; // no image selected on submit
  limitMsgs: string[] = []; // why selected files were skipped (format, size, file count)

  constructor(
    private uploader: DirectMediaUploadService,
    private router: Router,
    private formBuilder: FormBuilder,
    private sanitizer: DomSanitizer,
    public utils: UtilitiesService
  ) { }

  ngOnInit(): void {
    this.mediaForm = this.formBuilder.group({
      items: this.formBuilder.array([]),
    });
  }

  ngOnDestroy(): void {
    this.mediaItems.forEach(item => URL.revokeObjectURL(item.objectUrl));
  }

  get items(): FormArray {
    return this.mediaForm.get('items') as FormArray;
  }

  async onSelect(event): Promise<void> {
    this.limitMsgs = [];

    // The dropzone already filtered by UPLOAD_ACCEPT; those files only need a message.
    for (const file of (event.rejectedFiles || []) as File[]) {
      this.limitMsgs.push(this.utils.translate(
        '{0} is not a supported format. Supported formats: {1}',
        [file.name, this.SUPPORTED_FORMATS]
      ));
    }

    // Reject unsupported formats, oversized files and QuickTime movies named .mp4.
    const candidates: File[] = event.addedFiles || [];
    const rejections = await Promise.all(candidates.map(file => getUploadRejection(file)));
    let addedFiles = candidates.filter((_, i) => !rejections[i]);
    rejections.filter(Boolean).forEach(r => this.limitMsgs.push(this.utils.translate(r.key, r.values)));

    // Enforce the max-files-per-upload cap.
    const remainingSlots = Math.max(this.MAX_FILES - this.mediaItems.length, 0);
    if (addedFiles.length > remainingSlots) {
      const dropped = addedFiles.length - remainingSlots;
      addedFiles = addedFiles.slice(0, remainingSlots);
      this.limitMsgs.push(this.utils.translate(
        'You can upload up to {0} files (images or videos) per upload. {1} file(s) were not added.',
        [this.MAX_FILES, dropped]
      ));
    }

    addedFiles.forEach(file => {
      // An object URL rather than a data URL: a 100MB video read as base64 just for
      // a thumbnail would hold ~133MB of string per file.
      const objectUrl = URL.createObjectURL(file);
      this.mediaItems.push({
        file,
        objectUrl,
        previewUrl: this.sanitizer.bypassSecurityTrustUrl(objectUrl),
        isVideo: file.type.startsWith('video/'),
      });
      this.items.push(this.formBuilder.group({
        title: [this.titleFromFileName(file.name), [Validators.required, Validators.maxLength(this.TITLE_MAX)]],
        caption: ['', Validators.maxLength(this.CAPTION_MAX)], // optional
        alt_text: ['', Validators.maxLength(this.ALT_MAX)], // optional
      }));
    });

    if (this.mediaItems.length > 0) {
      this.imagesError = false;
    }
  }

  onRemove(index: number): void {
    URL.revokeObjectURL(this.mediaItems[index].objectUrl);
    this.mediaItems.splice(index, 1);
    this.items.removeAt(index);
  }

  async createMedia(): Promise<void> {
    this.mediaForm.markAllAsTouched();
    this.imagesError = this.mediaItems.length === 0;

    if (this.mediaForm.invalid || this.imagesError) {
      return;
    }

    this.isUploading = true;
    const queue = [...this.mediaItems];
    const failures: string[] = [];

    // One file at a time; each already sends its parts in parallel.
    for (const [n, item] of queue.entries()) {
      const { title, caption, alt_text } = this.items.at(this.mediaItems.indexOf(item)).value;
      const setStatus = (percent: number) => {
        this.uploadStatus = this.utils.translate('Uploading {0} of {1}… {2}%', [n + 1, queue.length, percent]);
      };
      setStatus(0);
      try {
        await this.uploader.upload(item.file, { title, caption, alt_text }, setStatus);
        // Drop uploaded files so retrying after a failure only resends the failed ones.
        this.onRemove(this.mediaItems.indexOf(item));
      } catch (error) {
        const reason = uploadErrorMessage(error);
        failures.push(reason ? `${item.file.name}: ${this.utils.translate(reason)}` : item.file.name);
      }
    }

    this.isUploading = false;
    this.uploadStatus = '';

    if (failures.length === 0) {
      this.utils.notify.showSuccess(this.utils.translate('Media uploaded successfully!'));
      this.router.navigate(['/dashboard/gallery']);
    } else {
      this.utils.notify.showError(
        this.utils.translate('{0} of {1} uploads failed. Please try again.', [failures.length, queue.length])
          + '\n' + failures.join('\n'),
        this.utils.translate('Upload Failed!')
      );
    }
  }

  // Prefill Title from the file name, dropping the extension (e.g. "hero_shot.jpg" -> "hero_shot").
  // Truncated to TITLE_MAX because the input's maxlength attribute only caps typing, not prefilled values.
  private titleFromFileName(name: string): string {
    const base = (name || '').trim();
    const dot = base.lastIndexOf('.');
    const withoutExt = dot > 0 ? base.slice(0, dot) : base;
    return withoutExt.slice(0, this.TITLE_MAX);
  }
}
